import { useState } from 'react';
import { Stack, Group, Button, TextInput, Modal, Tabs, ActionIcon, ScrollArea } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { Text } from './index';
import { usePreferencesStore } from '../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../hooks/useAccessibilityStyles';

interface StatusTemplate {
  emoji: string;
  label: string;
  message: string;
}

const STATUS_TEMPLATES: StatusTemplate[] = [
  { emoji: '💼', label: 'In a meeting', message: 'In a meeting' },
  { emoji: '🍕', label: 'Lunch break', message: 'Lunch break' },
  { emoji: '☕', label: 'Coffee break', message: 'Taking a coffee break' },
  { emoji: '🎯', label: 'Focus time', message: 'Focus time - Do not disturb' },
  { emoji: '🏖️', label: 'On vacation', message: 'On vacation' },
  { emoji: '🤒', label: 'Sick', message: 'Out sick' },
  { emoji: '🚗', label: 'Commuting', message: 'Commuting' },
  { emoji: '🏠', label: 'Working from home', message: 'Working from home' },
  { emoji: '📞', label: 'On a call', message: 'On a call' },
  { emoji: '🎓', label: 'In training', message: 'In training' },
];

const EMOJI_CATEGORIES = {
  'Smileys': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😗'],
  'Gestures': ['👍', '👎', '👏', '🙌', '👋', '🤝', '🙏', '✌️', '🤞', '🤟', '🤘', '👌', '🤏', '👈', '👉', '👆'],
  'Activities': ['⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸', '🏒', '🏑', '🥏', '🪀', '🎯', '🎮'],
  'Work': ['💼', '📊', '📈', '📉', '📱', '💻', '⌨️', '🖥️', '📞', '📧', '📝', '📋', '📁', '📂', '🗂️', '📅'],
  'Food': ['🍕', '🍔', '🍟', '🌭', '🍿', '🧇', '🥓', '🥚', '🍳', '🧈', '🥐', '🍞', '🥖', '🥨', '🧀', '☕'],
  'Travel': ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚛', '🚚', '🚜', '🚲', '🛴', '✈️'],
  'Objects': ['⏰', '⏲️', '⏱️', '🕰️', '🌡️', '🧭', '🧲', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💰', '🎁'],
};

interface EmojiStatusPickerProps {
  opened: boolean;
  onClose: () => void;
  onSave: (emoji: string, message: string) => void;
  currentEmoji?: string;
  currentMessage?: string;
  maxLength?: number;
}

/**
 * Emoji Status Picker Modal
 * Allows users to select emoji and write custom status messages
 */
export function EmojiStatusPicker({
  opened,
  onClose,
  onSave,
  currentEmoji = '',
  currentMessage = '',
  maxLength = 100,
}: EmojiStatusPickerProps) {
  const [selectedEmoji, setSelectedEmoji] = useState(currentEmoji);
  const [statusMessage, setStatusMessage] = useState(currentMessage);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const handleSave = () => {
    if (!statusMessage.trim()) {
      return;
    }
    onSave(selectedEmoji, statusMessage.trim());
    onClose();
  };

  const handleTemplateClick = (template: StatusTemplate) => {
    setSelectedEmoji(template.emoji);
    setStatusMessage(template.message);
  };

  const handleClear = () => {
    setSelectedEmoji('');
    setStatusMessage('');
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Set Status Message"
      size="lg"
    >
      <Stack gap="md">
        {/* Status Preview */}
        <Group gap="xs" style={{ padding: '0.5rem', backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}>
          {selectedEmoji && <Text size="xl" style={accessibilityStyles}>{selectedEmoji}</Text>}
          <Text size="sm" c={statusMessage ? 'dark' : 'dimmed'} style={accessibilityStyles}>
            {statusMessage || 'Enter your status message...'}
          </Text>
        </Group>

        {/* Templates Tab */}
        <Stack gap="xs">
          <Text size="sm" fw={600} style={accessibilityStyles}>Quick Templates</Text>
          <ScrollArea h={120}>
            <Stack gap="xs">
              {STATUS_TEMPLATES.map((template, index) => (
                <Button
                  key={index}
                  variant="light"
                  leftSection={<Text size="lg" style={accessibilityStyles}>{template.emoji}</Text>}
                  onClick={() => handleTemplateClick(template)}
                  fullWidth
                  justify="flex-start"
                >
                  {template.label}
                </Button>
              ))}
            </Stack>
          </ScrollArea>
        </Stack>

        {/* Emoji Picker */}
        <Stack gap="xs">
          <Text size="sm" fw={600} style={accessibilityStyles}>Choose Emoji</Text>
          <Tabs defaultValue="Smileys">
            <Tabs.List>
              {Object.keys(EMOJI_CATEGORIES).map((category) => (
                <Tabs.Tab key={category} value={category}>
                  {category}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
              <Tabs.Panel key={category} value={category} pt="xs">
                <ScrollArea h={120}>
                  <Group gap="xs">
                    {emojis.map((emoji) => (
                      <ActionIcon
                        key={emoji}
                        size="lg"
                        variant={selectedEmoji === emoji ? 'filled' : 'light'}
                        onClick={() => setSelectedEmoji(emoji)}
                      >
                        {emoji}
                      </ActionIcon>
                    ))}
                  </Group>
                </ScrollArea>
              </Tabs.Panel>
            ))}
          </Tabs>
        </Stack>

        {/* Custom Message Input */}
        <TextInput
          label="Status Message"
          placeholder="What's your status?"
          value={statusMessage}
          onChange={(e) => setStatusMessage(e.currentTarget.value)}
          maxLength={maxLength}
          description={`${statusMessage.length}/${maxLength} characters`}
          rightSection={
            statusMessage && (
              <ActionIcon size="sm" variant="subtle" onClick={handleClear}>
                <IconX size={14} />
              </ActionIcon>
            )
          }
        />

        {/* Actions */}
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconCheck size={16} />}
            onClick={handleSave}
            disabled={!statusMessage.trim()}
          >
            Save Status
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
